import type { ReactNode } from "react";
import { IconPlus } from "@tabler/icons-react";
import moment from "moment/moment";
// @ts-expect-error pure js
import useTenants from "../../hooks/use-tenants";
import Card from "../UI/Card";
import Callout from "../UI/Callout";
import ButtonCard from "../UI/ButtonCard";
import Button from "../UI/Button";
import Text from "../UI/Text";
// @ts-expect-error pure js
import { PlusBank } from "../UI/Icons";
// @ts-expect-error pure js
import { Profile as ProfileFactory, ProfileStatus } from "../../models/profile";
// @ts-expect-error pure js
import { getProfileDisplayName } from "../../utils/profiles";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import { TenantStatus } from "../../models/tenant_old.js";
import type { Profile } from "../Profile/types";
import styles from "./TenantAddProfile.module.css";

const SELECTABLE_TENANT_STATUSES: number[] = [
  TenantStatus.Actif.status,
  TenantStatus.Candidat.status,
];

// Une même personne existe souvent en plusieurs lignes profile (une par dossier :
// candidatures re-soumises, copies posées par les avenants) — on la regroupe par
// identité et rôle pour ne proposer qu'une entrée dans le select.
function personKey(profile: Profile) {
  return [
    getProfileDisplayName(profile).trim().toLowerCase(),
    profile.birthday ?? "",
    profile.garantor_of === null ? "locataire" : "garant",
  ].join("|");
}

interface TenantAddProfileProps {
  profiles: Profile[];
  label?: ReactNode;
  onSelect: (value: unknown) => void;
  onCreate: () => void;
  isMulti?: boolean;
  variant?: "card" | "inline";
}

export default function TenantAddProfile({
  profiles,
  label,
  onSelect,
  onCreate,
  isMulti = false,
  variant = "card",
}: TenantAddProfileProps) {
  const { tenants } = useTenants();

  // Par personne, on ne garde que le profil le plus récent : c'est lui qui porte
  // les dernières données saisies (dernière candidature, dernier avenant).
  const profilesByPerson = new Map<string, Profile>();
  tenants
    .filter((tenant: { status: number }) =>
      SELECTABLE_TENANT_STATUSES.includes(tenant.status),
    )
    .flatMap((tenant: { profiles: Profile[] }) =>
      tenant.profiles.filter(
        (p: Profile) => p.status === ProfileStatus.Actif.status,
      ),
    )
    .forEach((profile: Profile) => {
      const key = personKey(profile);
      const known = profilesByPerson.get(key);
      if (!known || (profile.id ?? 0) > (known.id ?? 0)) {
        profilesByPerson.set(key, profile);
      }
    });

  const profilesIndiv = [...profilesByPerson.values()].sort(
    (a: Profile, b: Profile) => {
      const nameA = getProfileDisplayName(a);
      const nameB = getProfileDisplayName(b);
      return nameA.localeCompare(nameB);
    },
  );

  // Reste homonyme après regroupement = vraies personnes distinctes : la date de
  // naissance dans le libellé permet de les différencier.
  const displayNameCounts = new Map<string, number>();
  profilesIndiv.forEach((profile: Profile) => {
    const name = getProfileDisplayName(profile);
    displayNameCounts.set(name, (displayNameCounts.get(name) ?? 0) + 1);
  });

  const profilesNonGarantOptions: Profile[] = [];
  const profilesGarantOptions: Profile[] = [];
  profilesIndiv.forEach((profile: Profile) => {
    if (profile.garantor_of === null) {
      profilesNonGarantOptions.push(ProfileFactory.create(profile));
    } else {
      profilesGarantOptions.push(ProfileFactory.create(profile));
    }
  });

  const profilesOptions = [
    { label: "Locataires", options: profilesNonGarantOptions },
    { label: "Garants", options: profilesGarantOptions },
  ];

  const isInline = variant === "inline";

  const selectField = (
    <ReactSelectStandalone
      value={isMulti && profiles}
      onChange={onSelect}
      isMulti={isMulti}
      options={profilesOptions}
      getOptionValue={(option: Profile) => option.id}
      getOptionLabel={(option: Profile) => {
        let name = getProfileDisplayName(option);
        if (!option.id) {
          name += ` (nouveau)`;
        } else if ((displayNameCounts.get(name) ?? 0) > 1 && option.birthday) {
          name += ` — né(e) le ${moment(option.birthday).format("DD/MM/YYYY")}`;
        }
        return name;
      }}
      label={
        isInline
          ? "Profil de locataire existant"
          : "Sélectionner un profil existant"
      }
    />
  );

  const minorWarning = profiles.some(
    (profile) => moment().diff(profile.birthday, "years") < 18,
  ) && (
    <Callout
      variant="warning"
      className={isInline ? styles.minorWarning : styles.minorWarningCard}
    >
      La date de naissance renseignée indique qu'au moins un des locataires est
      mineur. S'il n'est pas émancipé, le mineur reste titulaire du bail mais ce
      dernier doit être signé par ses représentants légaux. Ajoutez-les via le
      bouton « Ajouter un représentant légal » sur le profil du mineur : le bail
      mentionnera automatiquement la représentation et sera envoyé aux
      représentants pour signature, tandis que les quittances resteront au nom
      du mineur.
    </Callout>
  );

  if (isInline) {
    return (
      <div className={styles.inlineRoot}>
        <div className={styles.inlineCreateRow}>
          <Button
            icon={IconPlus}
            type="button"
            variant="secondary"
            onClick={onCreate}
          >
            Créer un profil de locataire
          </Button>
        </div>
        {selectField}
        {minorWarning}
      </div>
    );
  }

  return (
    <Card className={"marginB10"} padding={"md"}>
      <Text color="muted" className={styles.cardLabel}>
        {label}
      </Text>
      <ButtonCard
        className={"padding5 flex align-items-center marginB10 marginT20 w-100"}
        onClick={onCreate}
      >
        <PlusBank />
        <Text as="span" className={"marginL10"}>
          Créer un nouveau profil locataire
        </Text>
      </ButtonCard>
      {selectField}
      {minorWarning}
    </Card>
  );
}
